"use client";

import Image from "next/image";
import { useTenant } from "@/components/tenant/TenantProvider";

/**
 * Logo tenant untuk semua permukaan web (auth, sidebar, checkout, PWA).
 * logo_url ada → render gambar tenant; tidak ada → text-mark inisial brand
 * dengan warna --brand-primary. TIDAK pernah fallback ke /logo.webp — aset
 * statis itu bukan pembawa brand (satu build untuk semua tenant).
 */
interface BrandLogoProps {
  /** "mark" = ikon persegi saja (sidebar/PWA); "header" = logo + nama brand (halaman auth/checkout). */
  variant?: "mark" | "header";
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
}

export default function BrandLogo({
  variant = "header",
  width,
  height,
  className,
  priority,
}: BrandLogoProps) {
  const branding = useTenant();
  const name = branding.brand_name;
  const w = width ?? (variant === "mark" ? 32 : 140);
  const h = height ?? (variant === "mark" ? 32 : 36);

  if (branding.logo_url) {
    return (
      <Image
        src={branding.logo_url}
        alt={name}
        width={w}
        height={h}
        className={className ?? (variant === "mark" ? "h-8 w-8 object-contain" : "h-9 w-auto")}
        priority={priority}
      />
    );
  }

  const initial = (name.trim()[0] ?? "g").toUpperCase();
  const markBox = (
    <div
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-xl bg-[var(--brand-primary)] font-bold text-[var(--brand-on-primary,#fff)]"
      style={{ width: h, height: h, fontSize: Math.round(h * 0.5) }}
    >
      {initial}
    </div>
  );

  if (variant === "mark") {
    return (
      <span role="img" aria-label={name} className={className}>
        {markBox}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-2.5 ${className ?? ""}`}>
      {markBox}
      <span className="text-lg font-bold tracking-tight text-[var(--warm-black,#1a1a1a)]">
        {name}
      </span>
    </span>
  );
}
